/**
 * FENDecoder - Pure FEN-to-Board Decoder
 *
 * Decodes a Zodiac FEN string into an ISquare[][] board without
 * touching the singleton ZodiacEngine. Used by the Replay Viewer
 * to render boards at arbitrary positions.
 *
 * Parsing logic is adapted from FenLoader.ts FenToGameState().
 */

import { GameState, ISquare, IPiece, PieceType, Direction, Mode, State } from '../types';
import { Piece } from '../Piece';

const BOARD_ROWS = 8;
const BOARD_COLS = 5;

/**
 * Check if a character represents a piece type.
 */
function isPieceCharacter(char: string): boolean {
  return 'BCFGMUOPRSTVW'.includes(char.toUpperCase());
}

/**
 * Map a piece character to PieceType.
 */
function getPieceTypeFromChar(char: string): PieceType {
  const map: Record<string, PieceType> = {
    B: PieceType.Bear,
    C: PieceType.Crane,
    F: PieceType.Frog,
    G: PieceType.Goat,
    M: PieceType.Monkey,
    U: PieceType.Fungi,
    O: PieceType.Octopus,
    P: PieceType.Penguin,
    R: PieceType.Rhino,
    S: PieceType.Snake,
    T: PieceType.Tree,
    W: PieceType.Wolf,
    V: PieceType.Virus,
  };
  return map[char.toUpperCase()] || PieceType.Bear;
}

/**
 * Create an empty 8x5 board.
 */
function createEmptyBoard(): ISquare[][] {
  const board: ISquare[][] = [];
  for (let row = 0; row < BOARD_ROWS; row++) {
    const rowArr: ISquare[] = [];
    for (let col = 0; col < BOARD_COLS; col++) {
      rowArr.push({ Pieces: [] });
    }
    board.push(rowArr);
  }
  return board;
}

/**
 * Create a piece and apply special modifiers from the FEN piece string.
 * This replicates the logic from FenLoader.ts placePieceFromString()
 * but without any engine dependency.
 */
function createPieceFromString(pieceString: string, row: number, col: number): IPiece {
  const baseChar = pieceString[0];
  const pieceType = getPieceTypeFromChar(baseChar);
  const isLight = baseChar === baseChar.toUpperCase();

  // Default direction for penguins
  let direction: Direction | null = null;
  if (pieceType === PieceType.Penguin) {
    direction = isLight ? 'down' : 'up';
  }

  const piece = new Piece(pieceType, isLight, direction);

  // Frog lilypad: if not on starting row, lilypad used
  if (pieceType === PieceType.Frog) {
    const isOnStartingRow = (isLight && row === 0) || (!isLight && row === 7);
    piece.hasLilyJumped = !isOnStartingRow;
  }

  // Rhino: default to hasMoved unless on starting row with marker
  if (pieceType === PieceType.Rhino) {
    piece.hasMoved = true;
  }

  // Snake: default hasShed to true (ability already used)
  // ✳️ modifier will override to false if ability is still available
  if (pieceType === PieceType.Snake) {
    piece.hasShed = true;
  }

  // Parse modifiers
  for (let i = 1; i < pieceString.length; i++) {
    const ch = pieceString[i];

    switch (ch) {
      case '\u26D3': // ⛓ Entangled
        piece.isEntangled = true;
        break;
      case '\u2620':   // ☠ Poisoned
      case '\u2620\uFE0F':
        piece.poisonedStatus = { isPoisoned: true };
        break;
      case '#': // Meadowed
        piece.isMeadowed = true;
        break;
      case 'z': { // Zombified
        piece.zombifiedStatus.isZombified = true;
        piece.isZombified = true;
        let zombieTurn = '';
        while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
          zombieTurn += pieceString[++i];
        }
        if (zombieTurn) piece.zombifiedStatus.removeOnTurn = parseInt(zombieTurn, 10);
        break;
      }
      case 'i': piece.isInfected = true; break;
      case 'r': piece.isRiding = true; break;
      case 'h': piece.isHousing = true; break;
      case 'l': piece.isLone = true; break;
      case 'd': piece.isDormant = true; break;
      case '$':
        if (pieceType === PieceType.Wolf) piece.isLone = true;
        else if (pieceType === PieceType.Octopus) piece.isInfected = true;
        else if (pieceType === PieceType.Goat) piece.isFainted = true;
        else if (pieceType === PieceType.Monkey) piece.isRiding = true;
        break;
      case 'K': case 'k':
      case '\u265B': case '\u2655': // ♛ ♕ King markers
        piece.isKing = true;
        break;
      case '*':
      case '\u2733': // ✳ Ability unused
        if (pieceType === PieceType.Frog) piece.hasLilyJumped = false;
        else if (pieceType === PieceType.Rhino) piece.hasCharged = false;
        else if (pieceType === PieceType.Snake) piece.hasShed = false;
        break;
      case '\uFE0F': break; // Variation selector - ignore
      case '\u25CB': // ○ Rhino unmoved marker
        if (pieceType === PieceType.Rhino) {
          const rhinoOnStartingRow = (isLight && row === 0) || (!isLight && row === 7);
          if (rhinoOnStartingRow) piece.hasMoved = false;
        }
        break;
      // Penguin direction arrows
      case '\u2191': case '^': if (pieceType === PieceType.Penguin) piece.direction = 'up'; break;
      case '\u2197': case '}': if (pieceType === PieceType.Penguin) piece.direction = 'up-right'; break;
      case '\u2192': case '>': if (pieceType === PieceType.Penguin) piece.direction = 'right'; break;
      case '\u2198': case ')': if (pieceType === PieceType.Penguin) piece.direction = 'down-right'; break;
      case '\u2193': case '_': if (pieceType === PieceType.Penguin) piece.direction = 'down'; break;
      case '\u2199': case '(': if (pieceType === PieceType.Penguin) piece.direction = 'down-left'; break;
      case '\u2190': case '<': if (pieceType === PieceType.Penguin) piece.direction = 'left'; break;
      case '\u2196': case '{': if (pieceType === PieceType.Penguin) piece.direction = 'up-left'; break;
      case '!':
      case '\u2623': // ☣ Poisoned
        piece.poisonedStatus = { isPoisoned: true };
        {
          let poisonTurn = '';
          while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
            poisonTurn += pieceString[++i];
          }
          if (poisonTurn) piece.poisonedStatus.removeOnTurn = parseInt(poisonTurn, 10);
        }
        break;
      case '\u2698': // ⚘ Meadowed
        piece.isMeadowed = true;
        break;
      case '\u2620': // ☠ Zombified (alternative)
        piece.zombifiedStatus.isZombified = true;
        piece.isZombified = true;
        {
          let zt = '';
          while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
            zt += pieceString[++i];
          }
          if (zt) piece.zombifiedStatus.removeOnTurn = parseInt(zt, 10);
        }
        break;
      default: break; // Ignore unknown
    }
  }

  return piece;
}

/**
 * Strip metadata from a FEN string (bonus turn suffix, turn count, placement info).
 */
function stripFENMetadata(fen: string): { cleanFen: string; isDarkTurn: boolean } {
  let cleanFen = fen;

  // Remove bonus turn suffix (~:...)
  const bonusIdx = cleanFen.indexOf('~');
  if (bonusIdx !== -1) cleanFen = cleanFen.substring(0, bonusIdx);

  // Remove turn count metadata (@...)
  const atIdx = cleanFen.indexOf('@');
  if (atIdx !== -1) cleanFen = cleanFen.substring(0, atIdx);

  // Check dark turn prefix
  const isDarkTurn = cleanFen.startsWith('d');
  if (isDarkTurn) cleanFen = cleanFen.substring(1);

  return { cleanFen, isDarkTurn };
}

/**
 * Decode a Zodiac FEN string into a board array.
 * Pure function - no engine side effects.
 */
export function decodeFENToBoard(fenString: string): { board: ISquare[][]; isDarkTurn: boolean } {
  const { cleanFen, isDarkTurn } = stripFENMetadata(fenString);
  const board = createEmptyBoard();
  const columns = cleanFen.split('/');

  for (let colIndex = 0; colIndex < columns.length && colIndex < BOARD_COLS; colIndex++) {
    let rowIndex = 0;
    let i = 0;
    const colStr = columns[colIndex];

    while (i < colStr.length && rowIndex < BOARD_ROWS) {
      const char = colStr[i];

      // Number = empty squares
      if (!isNaN(parseInt(char))) {
        rowIndex += parseInt(char);
        i++;
        continue;
      }

      // Parse pieces
      let j = i;
      while (j < colStr.length && isNaN(parseInt(colStr[j])) && colStr[j] !== '/') {
        const currentChar = colStr[j];

        if (currentChar === '+') {
          j++;
          continue;
        }

        if (isPieceCharacter(currentChar)) {
          let pieceString = currentChar;
          j++;

          // Collect modifiers
          while (
            j < colStr.length &&
            isNaN(parseInt(colStr[j])) &&
            colStr[j] !== '/' &&
            colStr[j] !== '+' &&
            !isPieceCharacter(colStr[j])
          ) {
            pieceString += colStr[j];
            j++;
          }

          const piece = createPieceFromString(pieceString, rowIndex, colIndex);
          board[rowIndex][colIndex].Pieces.push(piece);

          // Move to next row unless next char is '+' (same square stacking)
          if (j < colStr.length && colStr[j] !== '+') {
            rowIndex++;
          }
        } else {
          j++;
        }
      }
      i = j;
    }
  }

  // Rebuild virus infection relationships
  for (let row = 0; row < BOARD_ROWS; row++) {
    for (let col = 0; col < BOARD_COLS; col++) {
      const square = board[row][col];
      const hasVirus = square.Pieces.some(p => p.type === PieceType.Virus);
      if (hasVirus && square.Pieces.length > 1) {
        for (const piece of square.Pieces) {
          if (piece.type !== PieceType.Virus) piece.isInfected = true;
        }
      }
    }
  }

  // Rebuild entanglement relationships
  const diags = [[-1, -1], [-1, 1], [1, 1], [1, -1]];
  for (let row = 0; row < BOARD_ROWS; row++) {
    for (let col = 0; col < BOARD_COLS; col++) {
      for (const piece of board[row][col].Pieces) {
        if (piece.type !== PieceType.Octopus) continue;
        for (const [dr, dc] of diags) {
          const ar = row + dr;
          const ac = col + dc;
          if (ar < 0 || ar >= BOARD_ROWS || ac < 0 || ac >= BOARD_COLS) continue;
          for (const adjPiece of board[ar][ac].Pieces) {
            if (adjPiece.isEntangled && adjPiece.isLight !== piece.isLight) {
              piece.entangledPieceId = adjPiece.id;
            }
          }
        }
      }
    }
  }

  // Rebuild housing relationships
  for (let row = 0; row < BOARD_ROWS; row++) {
    for (let col = 0; col < BOARD_COLS; col++) {
      const square = board[row][col];
      if (square.Pieces.length < 2) continue;
      const hasTree = square.Pieces.some(p => p.type === PieceType.Tree);
      if (!hasTree) continue;
      for (const piece of square.Pieces) {
        if (piece.type !== PieceType.Tree && piece.type !== PieceType.Virus && !piece.isHousing) {
          piece.isHousing = true;
        }
      }
    }
  }

  return { board, isDarkTurn };
}

/**
 * Strip king markers from a FEN string so kings appear as normal pieces.
 * Removes: ♛ ♕ K k (when used as modifiers, not piece chars)
 */
export function stripKingMarkers(fen: string): string {
  return fen
    .replace(/♛/g, '')
    .replace(/♕/g, '')
    // Only strip K/k when they follow a piece character (modifier position)
    .replace(/([BCFGMUOPRSTVWbcfgmuoprstvw])K/g, '$1')
    .replace(/([BCFGMUOPRSTVWbcfgmuoprstvw])k/g, '$1');
}

/**
 * Build a minimal synthetic GameState for replay board rendering.
 */
export function buildReplayGameState(
  fen: string,
  lightKing: string,
  darkKing: string,
  turnIndex: number,
  showKings: boolean
): GameState {
  const displayFen = showKings ? fen : stripKingMarkers(fen);
  const { board, isDarkTurn } = decodeFENToBoard(displayFen);

  // When showKings is true, mark the correct pieces as kings.
  // The FEN from multiplayer games typically doesn't include king markers
  // (they're secret), so we set isKing based on the known king types.
  if (showKings) {
    for (const row of board) {
      for (const square of row) {
        for (const piece of square.Pieces) {
          if (piece.isLight && piece.type === lightKing) {
            piece.isKing = true;
          } else if (!piece.isLight && piece.type === darkKing) {
            piece.isKing = true;
          }
        }
      }
    }
  }

  return {
    mode: Mode.Dev,
    board,
    state: State.Playing,
    lightKing: lightKing as PieceType,
    darkKing: darkKing as PieceType,
    isLightTurn: !isDarkTurn,
    turnCount: turnIndex,
    fenHistory: [],
    lightWon: undefined,
    activeBonusTurnPieceId: null,
  } as GameState;
}
